import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useUIStore } from '../store/uiStore'
import { useSessionStore } from '../store/sessionStore'

export default function Sidebar() {
  const location = useLocation()
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed)
  const toggleSidebar = useUIStore((state) => state.toggleSidebar)
  const setSidebarCollapsed = useUIStore((state) => state.setSidebarCollapsed)
  useSessionStore((state) => state.isAdmin) // keep subscription so nav updates after login/logout
  const [aboutOpen, setAboutOpen] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const BLOG_URL = 'https://example.com' // TODO: replace with your real blog URL

  // Initialize sidebar state based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      // Desktop: open by default (>= 640px), Mobile: collapsed by default (< 640px)
      const isMobile = window.innerWidth < 640
      setSidebarCollapsed(isMobile)
    }

    // Set initial state
    checkScreenSize()

    // Update on resize
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [setSidebarCollapsed])

  const navItems: Array<
    | { kind: 'link'; path: string; label: string; icon: string }
    | { kind: 'action'; label: string; icon: string; onClick: () => void }
  > = [
    { kind: 'link', path: '/', label: 'Home', icon: '⌂' },
    { kind: 'link', path: '/history', label: 'History', icon: '◄' },
    { kind: 'link', path: '/projects', label: 'Projects', icon: '▢' },
    { kind: 'action', label: 'About', icon: 'ⓘ', onClick: () => setAboutOpen(true) },
    // Use a text presentation selector (VS15) to keep icons consistent across platforms (avoid emoji-style glyphs).
    { kind: 'action', label: 'Contact', icon: '✉︎', onClick: () => setContactOpen(true) },
  ]

  // Admin page is accessible to all, but will prompt login if not admin.
  // Use a text presentation selector (VS15) to keep icons consistent across platforms (avoid emoji-style glyphs).
  navItems.push({ kind: 'link', path: '/admin', label: 'Admin', icon: '⚙︎' })

  const isActive = (path: string) => location.pathname === path

  return (
    <aside
      style={{
        width: sidebarCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
      }}
      className={[
        'h-screen',
        'border-r',
        'border-rule',
        'bg-paper2',
        'px-2',
        'py-3',
        'sm:px-3',
        'sm:py-4',
        'flex',
        'flex-col',
        'gap-3',
        'overflow-hidden',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2 px-1">
        <button
          className={[
            'h-9 w-9',
            'rounded-xl',
            'border',
            'border-rule',
            'bg-paper',
            'shadow-delphi',
            'text-ink',
            'grid',
            'place-items-center',
            'hover:bg-white/40',
            'transition',
          ].join(' ')}
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <span className="text-lg leading-none">{sidebarCollapsed ? '≡' : '≡'}</span>
        </button>
      </div>

      {!sidebarCollapsed && (
        <div className="px-2 pt-1">
          <Link
            to="/"
            className="font-serif text-xl sm:text-2xl tracking-tight text-ink hover:opacity-90 transition inline-block"
            aria-label="Home"
            title="Home"
          >
            Delphi
          </Link>
        </div>
      )}

      <nav className={['flex', 'flex-col', 'gap-2', 'px-1', 'pt-1'].join(' ')}>
        {navItems.map((item) => {
          const baseClass = [
            'flex',
            'items-center',
            sidebarCollapsed ? 'justify-center' : 'justify-start',
            'gap-3',
            'rounded-xl',
            'px-3',
            'py-2.5',
            'border',
            'border-transparent',
            item.kind === 'link' && isActive(item.path)
              ? 'bg-green/10 border-green/30 text-ink'
              : 'text-muted hover:bg-black/5 hover:text-ink',
            'transition',
          ].join(' ')

          if (item.kind === 'action') {
            return (
              <button key={item.label} type="button" className={baseClass} onClick={item.onClick}>
                <span className="text-lg w-5 text-center" aria-hidden="true">
                  {item.icon}
                </span>
                {!sidebarCollapsed && <span className="text-sm tracking-wide">{item.label}</span>}
              </button>
            )
          }

          return (
            <Link key={item.path} to={item.path} className={baseClass}>
              <span className="text-lg w-5 text-center" aria-hidden="true">
                {item.icon}
              </span>
              {!sidebarCollapsed && <span className="text-sm tracking-wide">{item.label}</span>}
            </Link>
          )
        })}
      </nav>

      {aboutOpen && (
        <div className="modal-overlay" onClick={() => setAboutOpen(false)}>
          <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-lg sm:text-xl font-semibold text-ink">About</h2>
              <button className="modal-close" type="button" onClick={() => setAboutOpen(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="text-sm text-muted leading-relaxed">
                <p>
                  This forecasting tool is inspired by academic research on AI forecasting, in particular it builds on{' '}
                  <a
                    className="text-ink underline-offset-2 hover:underline"
                    href="https://arxiv.org/html/2511.07678v1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Bridgewater research
                  </a>
                  . This{' '}
                  <a
                    className="text-ink underline-offset-2 hover:underline"
                    href={BLOG_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Placeholder link — update in Sidebar.tsx"
                  >
                    blog
                  </a>{' '}
                  accompanies the tool and explains a bit more about how it works.
                </p>
                <p className="mt-3">This is a hobby project and all the usual caveats about AI generated outputs apply.</p>
                <p className="mt-3">
                  If you'd like to reach out with any feedback, suggestions, request or comments or if you're building
                  something similar, please{' '}
                  <button
                    type="button"
                    className="text-ink underline-offset-2 hover:underline"
                    onClick={() => {
                      setAboutOpen(false)
                      setContactOpen(true)
                    }}
                  >
                    use the contact form
                  </button>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {contactOpen && (
        <div className="modal-overlay" onClick={() => setContactOpen(false)}>
          <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-header">
              <div />
              <button className="modal-close" type="button" onClick={() => setContactOpen(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="rounded-delphi border border-rule bg-paper overflow-hidden">
                <iframe
                  title="Contact form"
                  src="https://docs.google.com/forms/d/e/1FAIpQLScjQ6cB5D6L1NxQwNRw4BnTxp29fZbDJ2QycRCQEzD0UY8KUQ/viewform?embedded=true"
                  width="100%"
                  height="640"
                  frameBorder={0}
                  marginHeight={0}
                  marginWidth={0}
                >
                  Loading…
                </iframe>
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

